import React, { useMemo, useState } from "react";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { getLocale } from "@I18n/services/i18n";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import { useLevelUpInfo } from "@modules/ServiceProviders/hooks/useLevelUpInfo";
import { CSSIsolation } from "@UI/Contexts/CSSIsolation";
import { ThemeProvider } from "@UI/Contexts/ThemeProvider";
import {
  Wizard,
  WizardHeader,
  WizardContent,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import { Container } from "@UI/Components/Container/Container";
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Icon } from "@UI/Components/Icon/Icon";
import { Divider } from "@UI/Components/Divider/Divider";
import { SimpleGrid } from "@UI/Components/SimpleGrid/SimpleGrid";
import { Eye } from "@UI/Components/Icons/Eye";
import { MagnifyingGlassChart } from "@UI/Components/Icons/MagnifyingGlassChart";
import { Calendar } from "@UI/Components/Icons/Calendar";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { Gauge } from "@UI/Components/Gauge/Gauge";
import messages from "./SuperTalentMetrics/messages";
import { CharterCard } from "./SuperTalentMetrics/MetricCards/CharterCard";
import { VerifiedCard } from "./SuperTalentMetrics/MetricCards/VerifiedCard";
import { ProfilePicCard } from "./SuperTalentMetrics/MetricCards/ProfilePicCard";
import { TopPickCard } from "./SuperTalentMetrics/MetricCards/TopPickCard";
import { PromiseKeeperCard } from "./SuperTalentMetrics/MetricCards/PromiseKeeperCard";
import { LoyalClienteleCard } from "./SuperTalentMetrics/MetricCards/LoyalClienteleCard";
import { StarTalentCard } from "./SuperTalentMetrics/MetricCards/StarTalentCard";
import { ClientsClosedCard } from "./SuperTalentMetrics/MetricCards/ClientsClosedCard";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useServiceProviderMetrics } from "@modules/ServiceProviders/hooks/useServiceProviderMetrics";
import { IExtendedMetric } from "@modules/ServiceProviders/types/serviceProvider.types";
import { generateStripeAccountLink } from "@modules/ServiceProviders/services/generateStripeAccountLink";
import { useServiceProviderAdvantageLevel } from "@modules/ServiceProviders/hooks/useServiceProviderAdvantageLevel";
import { CurrentAdvantagesCard } from "./SuperTalentMetrics/CurrentAdvantagesCard";
import { HeaderSection } from "./SuperTalentMetrics/HeaderSection";
import { SuperTalentProfilePictureModal } from "./SuperTalentMetrics/SuperTalentProfilePictureModal";
import { useFeatureFlag } from "@Libs/FeatureFlags/hooks/useFeatureFlag";

const CONTAINER_MAX_W = [
  "container.md",
  "container.sm",
  "container.md",
  "container.xl",
];

const findMetric = (
  metrics: IExtendedMetric[],
  name: string
): IExtendedMetric | undefined => metrics.find((m) => m.name === name);

const isNumericReached = (m: IExtendedMetric | undefined): boolean => {
  if (!m || m.value === null || m.threshold === null) return false;
  return Number(m.value) >= Number(m.threshold);
};

const NextEvaluationPill = ({
  fontSize,
  date,
}: {
  fontSize: string;
  date: string;
}) => (
  <Flex bg="sand.200" alignItems="center" px={3} py={1} borderRadius="100px">
    <Text color="#666" as="span" fontWeight={400} fontSize={fontSize}>
      <FormattedMessage
        {...messages.nextEvaluation}
        values={{
          magnifyingGlass: () => (
            <Icon as={MagnifyingGlassChart} w={4} h={4} mr={1} color="#666" />
          ),
          nextComputedDate: () => <Text as="span" ml={1}>{date}</Text>,
        }}
      />
    </Text>
  </Flex>
);

const CalculatedOverText = ({ fontSize }: { fontSize: string }) => (
  <Text as="span" fontSize={fontSize}>
    <FormattedMessage
      {...messages.calculatedOver}
      values={{
        calendar: () => (
          <Icon as={Calendar} marginRight="4px" display="inline" />
        ),
      }}
    />
  </Text>
);

export const SuperTalentMetrics = () => {
  const { isMobile } = useMediaQuery();
  const locale = getLocale();

  const nextEvalDate = useMemo(() => {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return next.toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [locale]);

  const navigate = useNavigate();
  const { generateRoute } = useRoutes();

  const { levelUpInfo } = useLevelUpInfo();
  const kycInfo = levelUpInfo?.find((info) => info["key"] === "KYC");
  const isVerified = kycInfo?.state === "DONE";

  const [profilePicModalOpen, setProfilePicModalOpen] = useState(false);
  const [isKycLinkLoading, setIsKycLinkLoading] = useState(false);

  const { currentUser } = useCurrentUser({ includes: ["serviceProvider"] });
  const serviceProviderId = currentUser?.serviceProvider?.id;

  const handleVerifiedCardClick = async () => {
    if (!serviceProviderId || isKycLinkLoading) return;
    setIsKycLinkLoading(true);
    try {
      const url = await generateStripeAccountLink(serviceProviderId);
      window.location.href = url;
    } finally {
      setIsKycLinkLoading(false);
    }
  };
  const { isEnabled: isFilterRequestBySuperTalentMetricsEnabled } = useFeatureFlag(
    "filter_request_by_supertalent_metrics"
  );

  const { data: advantageData } = useServiceProviderAdvantageLevel(serviceProviderId);
  const advantageLevel = advantageData?.advantageLevel ?? "standard";
  const isPro = advantageData?.pro ?? false;
  const newbieBoostStatus = advantageData?.newbieBoostStatus ?? null;

  const { data: rawMetrics } = useServiceProviderMetrics(serviceProviderId);
  const metrics = (rawMetrics ?? []) as IExtendedMetric[];

  const charterMetric = findMetric(metrics, "respects_rules");
  const verifiedMetric = findMetric(metrics, "verified");
  const profilePicMetric = findMetric(metrics, "profile_picture_validation_status");
  const avatarSrc = profilePicMetric?.computationDetails?.profilePictureThumbUrl as string | undefined;
  const realisationMetric = findMetric(metrics, "realisation_rate");
  const selectionMetric = findMetric(metrics, "selection_rate");
  const repeatMetric = findMetric(metrics, "repeat_rate");
  const avgRatingMetric = findMetric(metrics, "average_rating");
  const clientsClosedMetric = findMetric(metrics, "clients_closed");

  // Reached state per criterion — also drives the metric cards' bottom-of-list sorting below.
  const charterReached = isNumericReached(charterMetric);
  const verifiedReached = isNumericReached(verifiedMetric);
  const profilePicReached = (profilePicMetric?.value ?? null) === "done";
  const selectionReached = selectionMetric !== undefined && selectionMetric.value !== null && Number(selectionMetric?.value ?? 0) >= Number(selectionMetric?.threshold ?? 20);
  const realisationReached = realisationMetric !== undefined && realisationMetric.value !== null && Number(realisationMetric?.value ?? 0) >= Number(realisationMetric?.threshold ?? 85);
  const repeatReached = repeatMetric !== undefined && repeatMetric.value !== null && Number(repeatMetric?.value ?? 0) >= Number(repeatMetric?.threshold ?? 20);
  const avgRatingReached = avgRatingMetric !== undefined && avgRatingMetric.value !== null && Number(avgRatingMetric?.value ?? 0) >= Number(avgRatingMetric?.threshold ?? 4.8);
  const clientsClosedReached = clientsClosedMetric !== undefined && clientsClosedMetric.value !== null && Number(clientsClosedMetric?.value ?? 0) >= (Number(clientsClosedMetric?.threshold) || 5);

  // Gauge score: count of 8 criteria reached (0–8)
  const gaugeScore = [
    charterReached,
    verifiedReached,
    profilePicReached,
    selectionReached,
    realisationReached,
    repeatReached,
    avgRatingReached,
    clientsClosedReached,
  ].filter(Boolean).length;

  return (
    <>
      <CSSIsolation>
        <ThemeProvider>
          <Wizard paddingTop={{ base: 5, md: 8 }}>
            <WizardHeader marginBottom={{ base: 5, md: 3 }} textAlign="start">
              <Container px={{ base: 0 }} maxW={CONTAINER_MAX_W}>
                <BackLink mb={5} />

                {/* Two-column header: description + current advantages */}
                <Flex
                  direction={{ base: "column", lg: "row" }}
                  gap={{ base: 6, lg: 10 }}
                  align={{ lg: "flex-start" }}
                >
                  <HeaderSection
                    advantageLevel={advantageLevel}
                    avatarSrc={avatarSrc}
                    isFilterRequestBySuperTalentMetricsEnabled={isFilterRequestBySuperTalentMetricsEnabled}
                  />

                  {advantageLevel !== "super_talent" && isFilterRequestBySuperTalentMetricsEnabled && (
                    <CurrentAdvantagesCard
                      advantageLevel={advantageLevel}
                      pro={isPro}
                      nextEvalDate={nextEvalDate}
                      newbieBoostStatus={newbieBoostStatus}
                      serviceProviderId={serviceProviderId}
                    />
                  )}
                </Flex>
              </Container>
            </WizardHeader>

            <WizardContent>
              <Container px={{ base: 0 }} maxW={CONTAINER_MAX_W}>
                <Divider borderColor="gray.200" mb={{ base: 6, md: 8 }} />

                {/* Goals section */}
                <Box mb={{ base: 6, md: 8 }}>
                  <Heading fontSize={{ base: "2xl", lg: "3xl" }} mb={{ base: 4, md: 1 }}>
                    <FormattedMessage {...messages.goalsTitle} />
                  </Heading>
                  <Text
                    fontSize="sm"
                    color="gray.400"
                    fontStyle="italic"
                    mb={{ base: 4, md: 3 }}
                  >
                    <FormattedMessage
                      {...messages.visibleOnlyToYou}
                      values={{
                        eyeIcon: () => (
                          <Icon as={Eye} marginRight="4px" display="inline" />
                        ),
                      }}
                    />
                  </Text>

                  <Text color="gray.600" mb={{ base: 4, md: 6 }}>
                    <FormattedMessage {...(isFilterRequestBySuperTalentMetricsEnabled ? messages.goalsDescription : messages.goalsDescriptionInBetween)} />
                  </Text>

                  <Flex
                    justify="center"
                    w="full"
                    mb={{ base: 4, md: 6 }}
                    overflow="visible"
                  >
                    <Gauge
                      score={gaugeScore}
                      variant={isMobile ? "circular" : "flat"}
                    />
                  </Flex>

                  {/* Calculated over — mobile only */}
                  <Flex
                    display={{ base: "flex", md: "none" }}
                    justifyContent="flex-end"
                  >
                    <CalculatedOverText fontSize="sm" />
                  </Flex>
                </Box>

                <Divider
                  display={{ base: "none", md: "block" }}
                  borderColor="gray.200"
                  mb={6}
                />

                {/* Evaluation row — desktop only */}
                <Flex
                  display={{ base: "none", md: "flex" }}
                  justify="space-between"
                  align="center"
                  mb={6}
                >
                  <NextEvaluationPill fontSize="md" date={nextEvalDate} />
                  <CalculatedOverText fontSize="md" />
                </Flex>

                {/* Metric cards grid — fixed order */}
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  {[
                    {
                      key: "charter",
                      node: (
                        <CharterCard
                          metric={charterMetric}
                          onClick={() => { window.location.href = generateRoute("CHART_URL"); }}
                        />
                      ),
                    },
                    {
                      key: "verified",
                      node: (
                        <VerifiedCard
                          metric={verifiedMetric}
                          onClick={!isVerified ? handleVerifiedCardClick : undefined}
                        />
                      ),
                    },
                    {
                      key: "profilePic",
                      node: (
                        <ProfilePicCard
                          metric={profilePicMetric}
                          onClick={() => setProfilePicModalOpen(true)}
                        />
                      ),
                    },
                    {
                      key: "selection",
                      node: (
                        <TopPickCard
                          metric={selectionMetric}
                          onClick={() => navigate(["SUPERTALENT_OBJECTIVES", "SELECTION_RATE"])}
                        />
                      ),
                    },
                    {
                      key: "realisation",
                      node: (
                        <PromiseKeeperCard
                          metric={realisationMetric}
                          onClick={() => navigate(["SUPERTALENT_OBJECTIVES", "REALISATION_RATE"])}
                        />
                      ),
                    },
                    {
                      key: "repeat",
                      node: (
                        <LoyalClienteleCard
                          metric={repeatMetric}
                          onClick={() => navigate(["SUPERTALENT_OBJECTIVES", "REPEAT_RATE"])}
                        />
                      ),
                    },
                    {
                      key: "avgRating",
                      node: (
                        <StarTalentCard
                          metric={avgRatingMetric}
                          onClick={() => { window.location.href = generateRoute("AVERAGE_RATING"); }}
                        />
                      ),
                    },
                    {
                      key: "clientsClosed",
                      node: (
                        <ClientsClosedCard
                          metric={clientsClosedMetric}
                          onClick={() => { window.location.href = generateRoute("JOBS_CLOSED"); }}
                        />
                      ),
                    },
                  ].map(({ key, node }) => (
                    <React.Fragment key={key}>{node}</React.Fragment>
                  ))}
                </SimpleGrid>

              </Container>
            </WizardContent>
          </Wizard>
        </ThemeProvider>
      </CSSIsolation>
      <SuperTalentProfilePictureModal
        isOpen={profilePicModalOpen}
        onClose={() => setProfilePicModalOpen(false)}
        metric={profilePicMetric}
        serviceProviderId={serviceProviderId}
      />
    </>
  );
};
